// CSS composition helpers for ordered main-style output and observed debug maps.
// They keep initial observed rules in source order and preserve debug marker ordering.
import type { LuisCompileDebugRule, LuisCompileResult } from '@lentystyle/compiler'
import { renderCssFromLeaves, type LuisRenderLeaf } from '@lentystyle/compiler/renderer'
import type { LuisDebugFeatureApi } from '../../features'

// Build observed-only debug metadata preserving observed rule order.
export function buildObservedDebugRules(
  debugRules: LuisCompileDebugRule[] | undefined,
  observedRules: Array<{ key: string }>,
): LuisCompileDebugRule[] | undefined {
  // Keep undefined when compile debug metadata is not available.
  if (!debugRules || debugRules.length === 0) {
    return undefined
  } else {
    // Continue observed debug-rule filtering.
  }

  const debugByKey = new Map(debugRules.map((rule) => [rule.key, rule]))
  const observedDebugRules: LuisCompileDebugRule[] = []
  // Keep observed rule-map output ordered by observed rule sequence.
  for (const observedRule of observedRules) {
    const matched = debugByKey.get(observedRule.key)
    if (matched) {
      observedDebugRules.push(matched)
    } else {
      // Continue lookup when current observed rule has no debug entry.
    }
  }

  // Return filtered observed debug metadata.
  return observedDebugRules
}

// Compose main CSS by keeping regular + active initial-observed rules in source order.
export function composeMainCssInSourceOrder(
  result: LuisCompileResult,
  activeInitialObservedRules: Array<{ key: string }>,
  remappedDebugRules: LuisCompileDebugRule[] | undefined,
  compileDebugEnabled: boolean,
  debugFeature: LuisDebugFeatureApi | undefined,
): string {
  // Fall back to legacy output when source-order payload is unavailable.
  if (!result.mainRules || !result.headerRules || typeof result.rootVariableRule !== 'string') {
    const initialObservedCss = activeInitialObservedRules
      .map((rule) => result.initialObservedRules?.find((entry) => entry.key === rule.key)?.rule ?? '')
      .filter(Boolean)
      .join('\n\n')
    // Return legacy regular + appended initial-observed composition.
    return initialObservedCss ? `${result.css}\n\n${initialObservedCss}` : result.css
  } else {
    // Continue source-ordered composition path.
  }

  const activeInitialKeys = new Set(activeInitialObservedRules.map((rule) => rule.key))
  const orderedRules = result.mainRules.filter((rule) => {
    // Keep regular rules always in output sequence.
    if (rule.observedMode === 'none') {
      return true
    } else if (rule.observedMode === 'initial') {
      // Keep initial-observed rules only when activation check passes.
      return activeInitialKeys.has(rule.key)
    } else {
      // Skip unknown mode values defensively.
      return false
    }
  })

  const pseudoLeaves: LuisRenderLeaf[] = orderedRules.map((rule) => ({
    key: rule.key,
    atRules: [...rule.atRules],
    selector: rule.selector,
    observedMode: 'none',
    sourceLine: rule.line,
    declarations: [],
  }))
  const overrides = new Map<string, string>(orderedRules.map((rule) => [rule.key, rule.rule]))
  const orderedBodyRules = renderCssFromLeaves([], pseudoLeaves, overrides)
  const baseCss = [...result.headerRules, result.rootVariableRule, ...orderedBodyRules].filter(Boolean).join('\n\n')

  // Return plain css when debug marker injection is disabled.
  if (!compileDebugEnabled || !debugFeature) {
    return baseCss
  } else {
    // Continue ordered debug marker injection.
  }

  const orderedDebugRules = buildDebugRulesByOrderedKeys(remappedDebugRules, orderedRules.map((rule) => rule.key))
  // Return css with ordered debug markers applied.
  return debugFeature.applyDebugRulesToCssMarkers(baseCss, orderedDebugRules) ?? baseCss
}

// Rebuild debug rules in the same order as target rule-key sequence.
export function buildDebugRulesByOrderedKeys(
  debugRules: LuisCompileDebugRule[] | undefined,
  orderedKeys: string[],
): LuisCompileDebugRule[] | undefined {
  // Keep undefined when compile debug metadata is not available.
  if (!debugRules || debugRules.length === 0) {
    return undefined
  } else {
    // Continue ordered debug-rule projection.
  }

  const debugByKey = new Map(debugRules.map((rule) => [rule.key, rule]))
  const orderedDebugRules: LuisCompileDebugRule[] = []
  // Project debug rules by requested key order.
  for (const key of orderedKeys) {
    const matched = debugByKey.get(key)
    if (matched) {
      orderedDebugRules.push(matched)
    } else {
      // Continue projection when key has no debug metadata.
    }
  }
  // Return ordered debug-rule sequence.
  return orderedDebugRules
}
